<template>
  <v-btn size="small" :color="buttonColor" :variant="buttonVariant" :class="{ 'cash-action--compact-mobile': compactMobile, 'cash-action--mobile-label': !!mobileButtonLabel }" :title="actionTitle" :aria-label="actionTitle" :disabled="!userId" @click="open"><v-icon icon="mdi-bank-transfer" size="18" class="mr-1 cash-action-icon" /><span class="cash-action-label">{{ actionTitle }}</span><span v-if="mobileButtonLabel" class="cash-action-mobile-label">{{ mobileButtonLabel }}</span></v-btn>
  <v-dialog v-model="dialog" max-width="520" :persistent="sending">
    <v-card :title="actionTitle">
      <form @submit.prevent="submit">
        <v-card-text>
          <v-alert v-if="error" type="error" variant="tonal" class="mb-3">{{ error }}</v-alert>
          <v-alert v-if="success" type="success" variant="tonal" class="mb-3">{{ success }}</v-alert>
          <v-alert v-if="pending" type="info" variant="tonal" class="mb-3">Offene Umbuchung: {{ pending.amount }} auf Konto {{ pending.targetAccountId ?? pending.accountId }}{{ pending.sourceAccountId != null ? ` von Konto ${pending.sourceAccountId}` : '' }}. „Erneut senden“ verwendet dieselbe Buchung.</v-alert>
          <v-select v-if="isAccountTransfer" v-model="sourceAccountId" :items="accounts" item-value="id" :item-title="accountLabel" label="Eigenes Ausgangskonto" :loading="loading" :disabled="sending || !!pending" variant="outlined" @update:model-value="accountId = null" />
          <v-select v-model="accountId" :items="targetAccounts" item-value="id" :item-title="accountLabel" :label="isAccountTransfer ? 'Zielkonto gleicher Währung' : 'Eigenes EUR-Zielkonto'" :loading="loading" :disabled="sending || !!pending" variant="outlined" />
          <v-text-field v-model="amount" :label="`Betrag${transferCurrency ? ' in ' + transferCurrency : ''}`" inputmode="decimal" :disabled="sending || !!pending" variant="outlined" hint="Positiver Betrag mit maximal zwei Nachkommastellen" persistent-hint />
          <p v-if="!loading && !accounts.length" class="text-caption mt-3">Keine passenden aktiven eigenen Geldkonten mit vorhandenem Kontostand verfügbar.</p>
          <p v-if="isAccountTransfer" class="text-caption">Cash bleibt unverändert. Die Buchung wird als Wealth-Umbuchung erfasst.</p>
          <v-btn v-if="loginRequired" variant="text" @click="reauthenticate">Erneut anmelden</v-btn>
        </v-card-text>
        <v-card-actions>
          <v-btn :disabled="sending" @click="dialog = false">Schließen</v-btn><v-spacer />
          <v-btn :disabled="sending || loading || loginRequired" :loading="sending" color="primary" type="submit">{{ pending ? 'Erneut senden' : 'Umbuchen' }}</v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api/client';
import { cashTransferAmount, cashTransferAccounts } from '@/utils/cashTransfer';

export default {
  name: 'CashToWealthAction',
  props: {
    transferMode: { type: String, default: 'cash' },
    buttonColor: { type: String, default: undefined },
    buttonVariant: { type: String, default: 'tonal' },
    compactMobile: { type: Boolean, default: false },
    mobileButtonLabel: { type: String, default: '' },
  },
  emits: ['completed'],
  data: () => ({ dialog: false, accounts: [], sourceAccountId: null, accountId: null, amount: '', pending: null, loading: false, sending: false, error: '', success: '', loginRequired: false }),
  computed: {
    userId() { return this.$store.getters.isAuthenticated ? Number(this.$store.getters.stateUser?.iduser) || null : null; },
    isAccountTransfer() { return this.transferMode === 'accounts'; },
    actionTitle() { return this.isAccountTransfer ? 'Konto zu Konto überweisen' : 'Cash auf Konto umbuchen'; },
    sourceAccount() { return this.accounts.find(account => account.id === this.sourceAccountId); },
    transferCurrency() { return this.isAccountTransfer ? this.sourceAccount?.currency : 'EUR'; },
    targetAccounts() { return this.isAccountTransfer ? this.accounts.filter(account => account.id !== this.sourceAccountId && account.currency === this.transferCurrency) : this.accounts; },
  },
  watch: {
    userId: { immediate: true, handler() { this.dialog = false; this.accounts = []; this.pending = null; this.sourceAccountId = null; this.accountId = null; this.amount = ''; this.error = ''; this.success = ''; this.restorePending(); } },
  },
  methods: {
    async reauthenticate() { const redirect = this.$route.fullPath; await this.$store.dispatch('logOut'); await this.$router.push({ path: '/login', query: { redirect } }); },
    key(id = this.userId) { return `${this.isAccountTransfer ? 'wealth-to-wealth' : 'cash-to-wealth'}:pending:v1:${id}`; },
    restorePending() {
      if (!this.userId) return;
      try {
        const saved = JSON.parse(window.localStorage.getItem(this.key()) || 'null');
        if (saved && (this.isAccountTransfer ? saved.sourceAccountId != null && saved.targetAccountId != null : saved.accountId != null) && cashTransferAmount(saved.amount) === saved.amount && typeof saved.requestId === 'string') {
          this.pending = saved; this.sourceAccountId = saved.sourceAccountId ?? null; this.accountId = saved.targetAccountId ?? saved.accountId; this.amount = saved.amount;
        }
      } catch { this.error = 'Die offene Umbuchung konnte nicht gelesen werden. Bitte den Browserspeicher prüfen.'; }
    },
    accountLabel(account) { return `${account.name} · ${account.latest_balance?.amount} ${account.currency}`; },
    async open() { this.success = ''; this.loginRequired = false; this.restorePending(); this.dialog = true; await this.loadAccounts(); },
    async loadAccounts() {
      const userId = this.userId; this.loading = true;
      try {
        const { data } = await api.get('wealth/accounts', { params: { owner_id: userId }, skipAuthRedirect: true });
        if (userId !== this.userId) return;
        if (!Array.isArray(data)) {
          this.accounts = [];
          this.error = 'Konten konnten nicht geladen werden.';
          return;
        }
        this.accounts = cashTransferAccounts(data, userId, this.isAccountTransfer ? null : 'EUR');
        if (!this.pending && !this.accounts.some(account => account.id === this.sourceAccountId)) this.sourceAccountId = null;
        if (!this.pending && !this.accounts.some(account => account.id === this.accountId)) this.accountId = null;
      } catch (error) { if (userId === this.userId) { this.error = this.message(error); this.loginRequired = error.response?.status === 401; } }
      finally { this.loading = false; }
    },
    message(error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) return detail.map(item => item.msg).join(' · ');
      if (error.response?.status === 401) return 'Bitte erneut anmelden. Die offene Umbuchung bleibt gespeichert.';
      return error.response ? 'Umbuchung fehlgeschlagen. Bitte Betrag und Zielkonto prüfen.' : 'Keine bestätigte Antwort erhalten. Bitte mit „Erneut senden“ dieselbe Umbuchung wiederholen.';
    },
    async submit() {
      if (this.sending || this.loading || !this.userId) return;
      const userId = this.userId;
      this.error = ''; this.success = ''; this.loginRequired = false;
      if (!this.pending) {
        const amount = cashTransferAmount(this.amount);
        if (!amount) { this.error = 'Bitte einen positiven Betrag mit maximal zwei Nachkommastellen eingeben.'; return; }
        if (!this.targetAccounts.some(account => account.id === this.accountId) || (this.isAccountTransfer && !this.sourceAccount)) { this.error = 'Bitte gültige eigene Geldkonten gleicher Währung auswählen.'; return; }
        try {
          const payload = { ...(this.isAccountTransfer ? { sourceAccountId: this.sourceAccountId, targetAccountId: this.accountId } : { accountId: this.accountId }), amount, requestId: crypto.randomUUID() };
          window.localStorage.setItem(this.key(userId), JSON.stringify(payload));
          this.pending = payload;
        } catch { this.error = 'Der Request konnte nicht sicher gespeichert werden. Es wurde keine Umbuchung gesendet.'; return; }
      }
      const payload = { ...this.pending }; this.sending = true;
      try {
        const response = await api.post(this.isAccountTransfer ? 'transactions/wealth-to-wealth' : 'transactions/cash-to-wealth', payload, { skipAuthRedirect: true });
        if (![200, 201].includes(response.status) || !response.data || typeof response.data !== 'object' || (!this.isAccountTransfer && !response.data.transactionId)) {
          if (userId === this.userId) this.error = 'Keine bestätigte Antwort erhalten. Bitte mit „Erneut senden“ dieselbe Umbuchung wiederholen.';
          return;
        }
        window.localStorage.removeItem(this.key(userId));
        if (userId !== this.userId) return;
        this.pending = null; this.amount = '';
        const result = response.data;
        this.success = this.isAccountTransfer ? `Überweisung bestätigt: ${result.amount ?? payload.amount} ${result.currency ?? this.transferCurrency}. Konto ${payload.sourceAccountId} → Konto ${payload.targetAccountId}.` : `Umbuchung bestätigt: ${result.amount} EUR. Cash: ${result.cashAfter} EUR · Konto: ${result.accountAfter} EUR.`;
        this.$emit('completed', result);
        const updates = await Promise.allSettled([this.loadAccounts(), ...(this.isAccountTransfer ? [] : [this.$store.dispatch('getStocks')]), this.$store.dispatch('getTransactions')]);
        if (userId === this.userId && updates.some(update => update.status === 'rejected')) this.error = 'Die Buchung ist bestätigt, aber nicht alle Ansichten konnten neu geladen werden. Bitte aktualisieren.';
      } catch (error) {
        if (userId !== this.userId) return;
        this.error = this.message(error); this.loginRequired = error.response?.status === 401;
        if ([404, 409, 422].includes(error.response?.status)) {
          window.localStorage.removeItem(this.key(userId)); this.pending = null;
          if (error.response.status === 404) await this.loadAccounts();
        }
      } finally { this.sending = false; }
    },
  },
};
</script>

<style scoped>
.cash-action-mobile-label {
  display: none;
}

@media (max-width: 600px) {
  .cash-action--compact-mobile .cash-action-mobile-label {
    display: inline;
  }

  .cash-action--compact-mobile.cash-action--mobile-label .cash-action-icon {
    display: none;
  }

  .cash-action--compact-mobile .cash-action-label {
    display: none;
  }

  .cash-action--compact-mobile .cash-action-icon {
    margin-right: 0 !important;
  }
}
</style>
